import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { ChevronDownIcon, ChevronUpIcon, ExclamationCircleIcon } from 'assets/icons';
import { CopyToClipboardButton } from 'components/CopyToClipboardButton';
import { getOtsType } from 'domains/catalog/Catalog.store';
import {
  CatalogButtons,
  getVehicleTitle,
  VehicleExpandedDetails,
} from 'pages/CatalogPage/common/CatalogBanner/CatalogVehicleTab';
import { CarDetailHeaderImage } from 'pages/CatalogPage/common/CatalogHeader/CatalogHeader';
import { Icon, Text, WithTooltip } from 'UI';
import { isQueryBMM } from 'utils';
import { useTablet } from 'utils/hooks/useBreakpoint';
import { Country } from 'utils/i18n/Country';
import {
  CATALOG_HEADER_IMAGE_HEIGHT,
  CATALOG_HEADER_IMAGE_WIDTH,
  CatalogExpandedSection,
  CatalogHeaderActions,
  CatalogHeaderContent,
  CatalogHeaderDivider,
  CatalogHeaderIdentityItem,
  CatalogHeaderIdentityLeft,
  CatalogHeaderImageSlot,
  CatalogHeaderTopGrid,
  CatalogHeaderWrapper,
  CatalogHeaderDesktopOptions,
  CatalogIconActionButton,
} from './CatalogVehicleHeader.styled';
import { CatalogVehicleOptionsMenu } from './CatalogVehicleOptionsMenu';
import { getOtsTypeColor } from './ots.utils';

export const CATALOG_HEADER_COLLAPSED_HEIGHT = 64;
export const CATALOG_HEADER_EXPANDED_EXTRA = 72;

export const CatalogVehicleHeader = ({
  result,
  vehicleImage,
  query,
  isExpanded,
  onExpandedChange,
  setIsChangeVehicle,
  connected = false,
}: {
  result: VehicleDetail;
  vehicleImage: string | undefined;
  query: string;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  setIsChangeVehicle?: (b: boolean) => void;
  connected?: boolean;
}) => {
  const { t } = useTranslation();
  const isTablet = useTablet();
  const isBMM = isQueryBMM(result.vehicleKey);
  const country = Country.findByKey(result.country);
  const vehicleTitle = getVehicleTitle(result, isBMM, t);
  const shouldShowExpandedDetails = isExpanded;
  const otsType = useSelector(getOtsType);
  const hasOts = !!(otsType?.data && otsType.data !== 'NONE');
  const otsCount = otsType?.count ?? 0;
  const otsIconColor = getOtsTypeColor(otsType?.data);

  return (
    <CatalogHeaderWrapper data-cy={'catalog-vehicle-header'} $connected={connected}>
      <CatalogHeaderTopGrid>
        <CatalogHeaderImageSlot>
          <CarDetailHeaderImage
            imageUrl={vehicleImage}
            catalogSource={result.catalogSource}
            isSmall
            maxWidth={CATALOG_HEADER_IMAGE_WIDTH}
            maxHeight={CATALOG_HEADER_IMAGE_HEIGHT}
          />
        </CatalogHeaderImageSlot>
        <CatalogHeaderContent>
          <CatalogHeaderIdentityLeft>
            <WithTooltip title={vehicleTitle} placement={'bottom'}>
              <Text type={'lead_text'} ellipsis noWrap data-cy={'vehicle-name'}>
                {vehicleTitle}
              </Text>
            </WithTooltip>
          </CatalogHeaderIdentityLeft>
          {!isBMM && result.vin && (
            <CatalogHeaderIdentityItem data-cy={'vin-row'}>
              <Text type={'text'} ellipsis noWrap data-cy={'vin'}>
                {t('catalog.parts.banner.vin', 'VIN: {{vin}}', { vin: result.vin })}
              </Text>
              <CopyToClipboardButton textToCopy={result.vin} size={20} />
            </CatalogHeaderIdentityItem>
          )}
          {result.vrn && country && (
            <CatalogHeaderIdentityItem data-cy={'vrn-row'}>
              <Icon IconComponent={country.icon} width={16} height={16} round noPointer />
              <Text type="base" bold data-cy={'vrn'} disableGutter noWrap>
                {result.vrn}
              </Text>
              <CopyToClipboardButton textToCopy={result.vrn} size={18} />
            </CatalogHeaderIdentityItem>
          )}
        </CatalogHeaderContent>
        <CatalogHeaderActions>
          {hasOts && (
            <WithTooltip
              title={
                otsCount === 1
                  ? t('catalog.parts.banner.ots.title.single', 'Vehicle under OTS.')
                  : t('catalog.parts.banner.ots.title', 'Vehicle under one or several OTS.')
              }
              placement={'bottom'}
            >
              <span data-cy={'ots-icon'}>
                <Icon IconComponent={ExclamationCircleIcon} color={otsIconColor} size={18} />
              </span>
            </WithTooltip>
          )}
          {isTablet ? (
            <>
              <CatalogVehicleOptionsMenu query={query} setIsChangeVehicle={setIsChangeVehicle} isCompact />
              <WithTooltip
                title={
                  isExpanded ? t('common.action.show_less', 'Show less') : t('common.action.show_more', 'Show more')
                }
                placement={'bottom'}
              >
                <CatalogIconActionButton
                  type={'button'}
                  onClick={() => onExpandedChange(!isExpanded)}
                  data-cy={'catalog-vehicle-expand-toggle'}
                  aria-expanded={isExpanded}
                >
                  <Icon
                    IconComponent={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                    size={22}
                    color={'grey15'}
                    noPointer
                  />
                </CatalogIconActionButton>
              </WithTooltip>
            </>
          ) : (
            <CatalogHeaderDesktopOptions data-cy={'catalog-vehicle-desktop-options'}>
              <CatalogButtons
                query={query}
                setIsChangeVehicle={setIsChangeVehicle}
                showExpandToggle={true}
                isShowFull={isExpanded}
                setIsShowFull={onExpandedChange}
              />
            </CatalogHeaderDesktopOptions>
          )}
        </CatalogHeaderActions>
      </CatalogHeaderTopGrid>
      {shouldShowExpandedDetails && (
        <>
          <CatalogHeaderDivider />
          <CatalogExpandedSection data-cy={'catalog-vehicle-header-expanded'}>
            <VehicleExpandedDetails result={result} isBMM={isBMM} layout={isTablet ? 'compact' : 'default'} />
          </CatalogExpandedSection>
        </>
      )}
    </CatalogHeaderWrapper>
  );
};
